import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/auth';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Avatar,
  Button,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PeopleIcon from '@mui/icons-material/People';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import EventIcon from '@mui/icons-material/Event';
import { addDefaultRotations, addLevelsFromSessions } from '../../store/competitionSlice';

const drawerWidth = 240;

const Sidebar = ({ open, onClose, onExport, onImportClick, onAddSession }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const dispatch = useDispatch();

  const competition = useSelector(state => 
    state.competition.competitions.find(c => c.competition?.id === id)
  );
  const hasDefaultRotation = competition?.rotations?.length > 0;

  const isCompetitionDetail = location.pathname.includes('/competition/') && id;

  const { isAdmin } = useAuth();

  const baseMenuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Create Competition', icon: <AddCircleIcon />, path: '/competition/create' },
    ...(isAdmin ? [{ text: 'Admin Panel', icon: <PeopleIcon />, path: '/admin' }] : []),
  ];

  const competitionMenuItems = isCompetitionDetail ? [
    ...(onAddSession ? [{
      text: 'Add Session',
      icon: <EventIcon />,
      action: () => {
        onAddSession();
        onClose();
      }
    }] : []),
    {
      text: 'Add Levels',
      icon: <AddCircleIcon />,
      action: () => {
        dispatch(addLevelsFromSessions({
          competitionId: id,
          showToast: true
        }));
        onClose();
      }
    },
    ...(!hasDefaultRotation ? [{
      text: 'Add Default Rotation',
      icon: <RotateRightIcon />,
      action: () => {
        dispatch(addDefaultRotations({
          competitionId: id,
          showToast: true
        }));
        onClose();
      }
    }] : []),
    {
      text: 'Import Data',
      icon: <FileUploadIcon />,
      action: () => {
        onImportClick();
        onClose();
      }
    },
    {
      text: 'Export Data',
      icon: <FileDownloadIcon />,
      action: () => {
        onExport();
        onClose();
      }
    }
  ] : [];

  return (
    <Drawer
      data-ui="nav-sidebar"
      variant="temporary"
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
        },
      }}
    >
      <List sx={{ mt: 8 }}>
        {baseMenuItems.map((item) => (
          <ListItemButton
            data-ui={`nav-sidebar-item-${item.text.toLowerCase().replace(/\s+/g, '-')}`}
            key={item.text}
            onClick={() => {
              navigate(item.path);
              onClose();
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        ))}

        {isCompetitionDetail && (
          <>
            <Divider sx={{ my: 2 }} />
            {competitionMenuItems.map((item) => (
              <ListItemButton
                data-ui={`nav-sidebar-action-${item.text.toLowerCase().replace(/\s+/g, '-')}`}
                key={item.text}
                onClick={item.action}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            ))}
          </>
        )}
      </List>
      <Box sx={{ 
        mt: 'auto', 
        p: 2, 
        borderTop: 1, 
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1
      }}>
        <UserProfile />
      </Box>
    </Drawer>
  );
};

export default Sidebar;
const UserProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (!user) return null;

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }} data-ui="nav-user-profile">
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          {user.email?.[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ overflow: 'hidden' }}>
          <Typography noWrap variant="subtitle2" data-ui="nav-user-email">
            {user.email}
          </Typography>
        </Box>
      </Box>
      <Button
        data-ui="btn-sign-out"
        variant="outlined"
        fullWidth
        onClick={handleLogout}
        size="small"
      >
        Sign Out
      </Button>
    </>
  );
};
